import {
    Box
} from "@mui/material";
import useDialogModal from "../../../hooks/useDialogModal";
import EmployeeInputForm from "./addEmployeeForm";
import { useCallback, useEffect, useReducer } from "react";
import EnhancedTable from "../../enhancedTable/EnhancedTable";
import { EmployeeService } from "../../../service/employeeService";
import { isLocalDev } from "../../../utils/environment";
import useAbortController from "../../../hooks/useAbortController";
import ConfirmationDialog from "../../ui/confirmationDialog";
import validLookup from "../../../utils/validLookup";
import { toast } from "react-toastify";
import EmployeeEditForm from "./editEmployeeForm";
import { employeeColumns } from './columns'
import EditIcon from "@mui/icons-material/Edit";
import { useCan } from "../../../hooks/useCan";
//import makeData from "./makeData";

export default function ManageEmployee() {
    const can = useCan();
    const [EmployeeInputDialog, showEmployeeInputForm] = useDialogModal(EmployeeInputForm);
    const [EmployeeEditDialog, showEmployeeEditForm] = useDialogModal(EmployeeEditForm);

    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        employees: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal, abortRequest] = useAbortController();

    useEffect(() => {
        return () => {
            abortRequest();
        }
    }, []);

    const onAddRecordClicked = () => {
        showEmployeeInputForm();
    }

    const handleRowEdit = (rowData) => {
        showEmployeeEditForm(null, { employeeRow: rowData });
    }

    const userAddHandler = (newUser) => {
        const newEmployees = state.employees.concat([newUser]);
        updateState({
            employees: newEmployees,
            totalRowCount: state.totalRowCount + 1,
            pageCount: state.pageCount || 1,
        });
    }

    const userEditHandler = (user) => {
        // find the employee by row id
        const existingEmployees = state.employees;
        const idx = existingEmployees.findIndex(e => e.id === user.id);
        const updatesList = existingEmployees.map((row, index) => {
            if (row.id === user.id) {
                return {
                    ...existingEmployees[idx], ...user,
                }
            }
            return row
        });

        // update list
        updateState({
            employees: updatesList
        });
    }

    /**
     * fetch data
     */
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        EmployeeService.getWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    employees: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.log(error)
            }
            updateState({ loading: false });
        });
    }, []);

    /**
     * delete
     * @param {*} rowids 
     */
    const onDeleteUser = (rowids) => {
        // Check if user has delete permission
        if (!can.section('employees', { action: 'delete' })) {
            toast.error('You do not have permission to delete employees');
            return;
        }

        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await EmployeeService.deleteEmployee(rowids.map(x => parseInt(x, 10)));
                const { code, message, data } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.employees.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ employees: updateList });
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    // Check permissions for various actions
    const canDelete = can.section('employees', { action: 'delete' });
    const canCreate = can.section('employees', { action: 'create' });
    const canUpdate = can.section('employees', { action: 'edit' });

    return (
        <Box>
            <EnhancedTable
                columns={employeeColumns}
                addRecordHandler={canCreate ? onAddRecordClicked : undefined}
                deleteRecordHandler={canDelete ? onDeleteUser : undefined}
                data={state.employees}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                selectionMode="multi"
                loading={state.loading}
                actions={canUpdate ? [
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                ] : []}
            />
            <EmployeeInputDialog onUserAdded={userAddHandler} />
            <EmployeeEditDialog onUserEdit={userEditHandler} />
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>

    )

}